import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import { useIntl } from 'react-intl'
import { CompanyListItem, SelectItem } from '../../charts/core/_models'
import { companyOptionsList, companySitesList } from '../../charts/core/_requests'
import { Button } from 'react-bootstrap'
import { DateRangeSelector } from '../../../utils/Form/Widgets/DateRangeInput'
import moment from 'moment'
import { Range } from 'react-date-range'
import { getSiteFeatureOptions } from '../core/_requests'

type Props = {
  companyFilter: SingleValue<SelectItem>
  setCompanyFilter: Dispatch<SetStateAction<SingleValue<SelectItem>>>
  siteFilter: MultiValue<SelectItem>
  setSiteFilter: Dispatch<SetStateAction<MultiValue<SelectItem>>>
  period: string
  setPeriod: Dispatch<SetStateAction<string>>
  startDate: moment.Moment
  setStartDate: Dispatch<SetStateAction<moment.Moment>>
  endDate: moment.Moment
  setEndDate: Dispatch<SetStateAction<moment.Moment>>
  selectedFeature: string
  setSelectedFeature: Dispatch<SetStateAction<string>>
  setSelectedFeatureName: Dispatch<
    SetStateAction<{
      name: string
      unit: string
    }>
  >
}
const BenchmarkRecordFilter: React.FC<Props> = ({
  companyFilter,
  setCompanyFilter,
  siteFilter,
  setSiteFilter,
  period,
  setPeriod,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedFeature,
  setSelectedFeature,
  setSelectedFeatureName,
}) => {
  const [selectedCompany, setSelectedCompany] = useState<SingleValue<CompanyListItem>>(null)
  const [selectedSite, setSelectedSite] = useState<MultiValue<CompanyListItem>>([])
  const [companyOptions, setCompanyOptions] = useState<CompanyListItem[]>([])
  const [companySiteOptions, setCompanySiteOptions] = useState<CompanyListItem[]>([])
  const [selectedRange, setSelectedRange] = useState<Range>({
    startDate: moment().subtract(7, 'days').toDate(),
    endDate: moment().toDate(),
    key: 'selection',
  })
  const [featureOptions, setFeatureOptions] = useState<any>([])

  const intl = useIntl()

  const fetchCompanySiteFeatures = async (companyId: number) => {
    try {
      const response = await getSiteFeatureOptions(companyId).then((response) => {
        if (!response) {
          setFeatureOptions([])
        } else {
          setFeatureOptions(response)
        }
      })
    } catch (error) {
      console.error('Failed to fetch features:', error)
      return []
    }
  }

  useEffect(() => {
    companyOptionsList().then(setCompanyOptions)
    if (companyFilter) {
      fetchCompanySiteFeatures(Number(companyFilter.value))
    }
  }, [])

  useEffect(() => {
    setSelectedCompany(companyFilter)
    if (companyFilter) {
      fetchCompanySiteFeatures(Number(companyFilter.value))
    }
  }, [companyFilter])

  useEffect(() => {
    if (selectedCompany) {
      companySitesList({ companyId: Number(selectedCompany.value) }).then(setCompanySiteOptions)
    }
  }, [selectedCompany])

  useEffect(() => {
    setSelectedSite(siteFilter)
  }, [siteFilter])
  let placeholderSite = intl.formatMessage({ id: 'SELECTSITES' })

  const handlePeriodChange = (selectedPeriod: string) => () => {
    setPeriod(selectedPeriod)
    if (selectedPeriod === 'this_year') {
      setStartDate(moment().startOf('year'))
      setEndDate(moment())
    }
    if (selectedPeriod === 'last_one_month') {
      setStartDate(moment().subtract(1, 'month'))
      setEndDate(moment())
    }
    if (selectedPeriod === 'last_one_week') {
      setStartDate(moment().subtract(1, 'week'))
      setEndDate(moment())
    }
    if (selectedPeriod === 'custom') {
      setStartDate(moment(selectedRange.startDate))
      setEndDate(moment(selectedRange.endDate))
    }
  }

  const handleRangeSelection = (range: Range) => {
    if (range.startDate && range.endDate) {
      const newRange = {
        startDate: range.startDate,
        endDate: range.endDate,
        key: 'selection',
      }
      setSelectedRange(newRange)
      setStartDate(moment(range.startDate))
      setEndDate(moment(range.endDate))
    }
  }

  return (
    <div className='card'>
      <div className='card-body'>
        <div className='row'>
          <div className='col-lg-3'>
            <label htmlFor='site-select'>
              {' '}
              {intl.formatMessage({ id: 'FILTERFORM.COMPANY' })}:
            </label>
            <Select
              defaultValue={companyOptions.find((option) => option.value === companyFilter?.value)}
              onChange={setCompanyFilter}
              options={companyOptions}
              placeholder={'Select Company'}
              value={selectedCompany}
              isDisabled={true}
            />
          </div>

          {companyFilter && (
            <div className='col-lg-3'>
              <label htmlFor='site-select'>
                {intl.formatMessage({ id: 'FILTERFORM.SITESELECT' })}:
              </label>
              <Select
                defaultValue={siteFilter}
                onChange={setSiteFilter}
                options={companySiteOptions}
                isMulti={true}
                placeholder={placeholderSite}
                value={siteFilter}
              />
            </div>
          )}
          {companyFilter && (
            <div className='col-lg-3'>
              <label htmlFor='period-buttons'>
                {intl.formatMessage({ id: 'BENCHMARK.PERIOD' })}:
              </label>
              <div id='period-buttons' className='d-inline-flex' style={{ gap: '10px' }}>
                <Button
                  variant={period === 'this_year' ? 'primary' : 'secondary'}
                  style={{ minWidth: '120px', maxHeight: '40px' }}
                  onClick={handlePeriodChange('this_year')}
                >
                  {intl.formatMessage({ id: 'BENCHMARK.THIS_YEAR' })}
                </Button>
                <Button
                  variant={period === 'last_one_month' ? 'primary' : 'secondary'}
                  style={{ minWidth: '120px', maxHeight: '40px' }}
                  onClick={handlePeriodChange('last_one_month')}
                >
                  {intl.formatMessage({ id: 'BENCHMARK.LAST_MONTH' })}
                </Button>
                <Button
                  variant={period === 'last_one_week' ? 'primary' : 'secondary'}
                  style={{ minWidth: '120px', maxHeight: '40px' }}
                  onClick={handlePeriodChange('last_one_week')}
                >
                  {intl.formatMessage({ id: 'BENCHMARK.LAST_WEEK' })}
                </Button>
                <Button
                  variant={period === 'custom' ? 'primary' : 'secondary'}
                  style={{ minWidth: '120px', maxHeight: '40px' }}
                  onClick={handlePeriodChange('custom')}
                >
                  {intl.formatMessage({ id: 'BENCHMARK.CUSTOM' })}
                </Button>
              </div>
              {period === 'custom' && (
                <div style={{ minWidth: '250px', height: '60px', marginTop: '10px' }}>
                  <DateRangeSelector
                    name='custom_range'
                    title='Date Range'
                    seperator='to'
                    placeholder='Select Date Range'
                    ranges={[
                      {
                        startDate: startDate.toDate(),
                        endDate: endDate.toDate(),
                        key: 'selection',
                      },
                    ]}
                    rangeSelected={handleRangeSelection}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Feature Options */}
      <div className='row g-2'>
        {featureOptions != null &&
          featureOptions.map((feature: any) => (
            <div key={feature.value} className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2'>
              <div
                className={`feature-card ${
                  selectedFeature === feature.value ? 'feature-card-selected' : ''
                }`}
                onClick={() => {
                  setSelectedFeature(feature.value)
                  setSelectedFeatureName({ name: feature.label, unit: feature.unit })
                }}
              >
                <div className='feature-card-icon'>
                  <i className='fas fa-chart-bar'></i>
                </div>
                <div className='feature-card-content'>
                  <h6 className='feature-card-title'>{feature.label}</h6>
                  {feature.unit && <span className='feature-card-unit'>({feature.unit})</span>}
                </div>
                {selectedFeature === feature.value && (
                  <div className='feature-card-check'>
                    <i className='fas fa-check-circle'></i>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }

        .react-select-container .react-select__control {
          border-color: #ced4da;
          min-height: 38px;
        }

        .react-select-container .react-select__control:hover {
          border-color: #adb5bd;
        }

        .react-select-container .react-select__control--is-focused {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        /* Feature Card Styles */
        .feature-card {
          background: white;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          min-height: 90px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .feature-card:hover {
          border-color: #007bff;
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(0,123,255,0.15);
        }

        .feature-card-selected {
          border-color: #007bff !important;
          background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
          box-shadow: 0 3px 8px rgba(0,123,255,0.2);
        }

        .feature-card-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #007bff, #0056b3);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }

        .feature-card-selected .feature-card-icon {
          background: linear-gradient(135deg, #28a745, #20c997);
        }

        .feature-card-icon i {
          color: white;
          font-size: 14px;
        }

        .feature-card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .feature-card-title {
          font-size: 12px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 2px;
          line-height: 1.2;
        }

        .feature-card-selected .feature-card-title {
          color: #007bff;
        }

        .feature-card-unit {
          font-size: 10px;
          color: #6c757d;
          font-weight: 500;
        }

        .feature-card-selected .feature-card-unit {
          color: #007bff;
        }

        .feature-card-check {
          position: absolute;
          top: 4px;
          right: 4px;
          color: #28a745;
          font-size: 14px;
          animation: checkmark 0.3s ease-in-out;
        }

        @keyframes checkmark {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Responsive adjustments */
        @media (max-width: 991.98px) {
          .btn-group .btn {
            font-size: 0.8rem;
            padding: 0.375rem 0.5rem;
          }
          
          .feature-card {
            min-height: 80px;
            padding: 10px;
          }
          
          .feature-card-icon {
            width: 28px;
            height: 28px;
            margin-bottom: 6px;
          }
          
          .feature-card-icon i {
            font-size: 12px;
          }
        }

        @media (max-width: 575.98px) {
          .d-flex.gap-2 {
            flex-direction: column;
          }

          .flex-fill {
            width: 100%;
          }
          
          .feature-card {
            min-height: 70px;
            padding: 8px;
          }
          
          .feature-card-title {
            font-size: 11px;
          }
          
          .feature-card-unit {
            font-size: 9px;
          }
        }

        /* Pulse effect for selected card */
        .feature-card-selected::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(45deg, #007bff, #0056b3);
          border-radius: 10px;
          z-index: -1;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            opacity: 0.7;
          }
          50% {
            opacity: 0.4;
          }
          100% {
            opacity: 0.7;
          }
        }
      `}</style>
    </div>
  )
}
export default BenchmarkRecordFilter
